import { displayInitial } from '../../utils/displayInitial'
import { useEffect, useState } from 'react'
import { adminApi } from '../../services/api'
import { mediaUrl } from '../../services/config'
import { dialog } from '../../services/dialog'
import { Shield, Users, Server, BarChart3, X, Mail, Trash2, MessageSquare, Headphones, Pencil } from 'lucide-react'

interface ServerItem {
  id: string
  name: string
  iconUrl: string | null
  owner: { id: string; username: string; email: string }
  _count: { members: number; channels: number }
  createdAt: string
}

interface UserItem {
  id: string
  username: string
  email: string
  avatarUrl: string | null
  status: string
  role: string
  isAdmin: boolean
  createdAt: string
  firstName?: string
  lastName?: string | null
  aboutMe?: string | null
  serverMembers: { server: { id: string; name: string }; roles: { role: { id: string; name: string } }[] }[]
}

interface InviteItem {
  id: string
  email: string
  serverId: string
  server: { id: string; name: string } | null
  usedAt: string | null
  createdAt: string
}

interface DashboardStats {
  totalUsers: number
  totalServers: number
  totalChannels: number
  totalMessages: number
  totalVoiceSessions: number
  totalVoiceDuration: number
  totalStorage: number
}

export default function AdminPanel({ onClose }: { onClose: () => void }) {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [servers, setServers] = useState<ServerItem[]>([])
  const [users, setUsers] = useState<UserItem[]>([])
  const [invites, setInvites] = useState<InviteItem[]>([])
  const [activeTab, setActiveTab] = useState<'overview' | 'servers' | 'users' | 'invites'>('overview')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingUser, setEditingUser] = useState<UserItem | null>(null)
  const [editingServer, setEditingServer] = useState<ServerItem | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    setError('')
    try {
      const [dashRes, serversRes, usersRes, invitesRes] = await Promise.all([
        adminApi.getDashboard().catch(() => ({ data: null })),
        adminApi.getServers().catch(() => ({ data: [] })),
        adminApi.getUsers().catch(() => ({ data: [] })),
        adminApi.getInvites().catch(() => ({ data: [] })),
      ])
      setStats(dashRes.data)
      setServers(serversRes.data)
      setUsers(usersRes.data)
      setInvites(invitesRes.data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load admin data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteServer = async (id: string) => {
    if (!(await dialog.confirm({ message: 'Delete this server? This cannot be undone.', tone: 'destructive', confirmLabel: 'Delete' }))) return
    try {
      await adminApi.deleteServer(id)
      setServers((prev) => prev.filter((s) => s.id !== id))
    } catch (err) {
      console.error('Failed to delete server:', err)
    }
  }

  const handleDeleteInvite = async (id: string) => {
    try {
      await adminApi.deleteInvite(id)
      setInvites((prev) => prev.filter((i) => i.id !== id))
    } catch (err) {
      console.error('Failed to delete invite:', err)
    }
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-card rounded-lg p-6 w-[700px] max-h-[80vh] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-foreground font-bold text-lg flex items-center gap-2">
              <Shield size={20} className="text-destructive" />
              Admin Panel
            </h2>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
          </div>
          <p className="text-destructive text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg w-[800px] max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="h-12 px-4 flex items-center justify-between border-b border-border shrink-0">
          <h2 className="text-foreground font-bold text-lg flex items-center gap-2">
            <Shield size={20} className="text-yellow-500" />
            Global Admin
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border shrink-0">
          {([
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'servers', label: 'Servers', icon: Server },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'invites', label: 'Invites', icon: Mail },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'text-foreground bg-accent border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : (
            <>
              {activeTab === 'overview' && stats && (
                <div className="grid grid-cols-3 gap-3">
                  <StatCard label="Users" value={stats.totalUsers} icon={Users} color="text-primary" />
                  <StatCard label="Servers" value={stats.totalServers} icon={Server} color="text-green-500" />
                  <StatCard label="Channels" value={stats.totalChannels} icon={MessageSquare} color="text-yellow-500" />
                  <StatCard label="Messages" value={stats.totalMessages} icon={MessageSquare} color="text-destructive" />
                  <StatCard label="Voice Sessions" value={stats.totalVoiceSessions} icon={Headphones} color="text-primary" />
                  <StatCard label="Voice Hours" value={Math.floor((stats.totalVoiceDuration || 0) / 3600)} icon={Headphones} color="text-green-500" />
                </div>
              )}

              {activeTab === 'servers' && (
                <div className="space-y-2">
                  {servers.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-accent transition">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-foreground text-xs font-bold">
                        {displayInitial(s.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-foreground text-sm font-medium">{s.name}</div>
                        <div className="text-muted-foreground text-xs">Owner: {s.owner?.username || 'Unknown'}</div>
                      </div>
                      <div className="text-muted-foreground text-xs flex gap-3">
                        <span>{s._count?.members || 0} members</span>
                        <span>{s._count?.channels || 0} channels</span>
                      </div>
                      <button
                        onClick={() => setEditingServer(s)}
                        className="text-muted-foreground hover:text-foreground transition"
                        title="Edit server (name / owner)"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteServer(s.id)}
                        className="text-muted-foreground hover:text-destructive transition"
                        title="Delete server"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {servers.length === 0 && <p className="text-muted-foreground text-sm">No servers</p>}
                </div>
              )}

              {activeTab === 'users' && (
                <div className="space-y-1">
                  {users.map((u) => (
                    <div key={u.id} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-accent transition">
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-foreground text-xs font-bold">
                        {u.avatarUrl ? <img src={mediaUrl(u.avatarUrl)} alt="" className="w-full h-full rounded-full" /> : displayInitial(u.username)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-foreground text-sm font-medium">{u.username}</div>
                        <div className="text-muted-foreground text-xs">{u.email}</div>
                        <div className="text-muted-foreground text-[10px]">
                          {u.serverMembers?.map((sm) => sm.server.name).join(', ') || 'No servers'}
                        </div>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        u.status === 'ONLINE' ? 'bg-green-500/20 text-green-500' : 'bg-muted-foreground/20 text-muted-foreground'
                      }`}>
                        {u.status}
                      </span>
                      {u.isAdmin && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-500">Admin</span>
                      )}
                      <button
                        onClick={() => setEditingUser(u)}
                        title="Edit user"
                        className="p-1 text-muted-foreground hover:text-foreground transition"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={async () => {
                          if (!(await dialog.confirm({ message: `Delete user ${u.username}? This is permanent.`, tone: 'destructive', confirmLabel: 'Delete' }))) return
                          try {
                            await adminApi.deleteUser(u.id)
                            setUsers((prev) => prev.filter((x) => x.id !== u.id))
                          } catch (err: any) {
                            void dialog.alert({ message: err.response?.data?.error || 'Failed to delete user', tone: 'destructive' })
                          }
                        }}
                        title="Delete user"
                        className="p-1 text-muted-foreground hover:text-destructive transition"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'invites' && (
                <div className="space-y-2">
                  {invites.map((i) => (
                    <div key={i.id} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-accent transition">
                      <Mail size={16} className="text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="text-foreground text-sm">{i.email}</div>
                        <div className="text-muted-foreground text-xs">
                          Server: {i.server?.name || i.serverId} {i.usedAt ? '· Used' : '· Pending'}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteInvite(i.id)}
                        className="text-muted-foreground hover:text-destructive transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {invites.length === 0 && <p className="text-muted-foreground text-sm">No invites</p>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={(updated) => {
            setUsers((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
            setEditingUser(null)
          }}
        />
      )}
      {editingServer && (
        <ServerEditModal
          server={editingServer}
          users={users}
          onClose={() => setEditingServer(null)}
          onSaved={(updated) => {
            setServers((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
            setEditingServer(null)
          }}
        />
      )}
    </div>
  )
}

// OSPROD-209: global admin renames a server + reassigns its owner. Owner is
// picked from the already-loaded users list (no extra fetch). The server-side
// PATCH validates the new owner and adds them as a member.
function ServerEditModal({
  server,
  users,
  onClose,
  onSaved,
}: {
  server: ServerItem
  users: UserItem[]
  onClose: () => void
  onSaved: (s: ServerItem) => void
}) {
  const [name, setName] = useState(server.name)
  const [ownerId, setOwnerId] = useState(server.owner?.id || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const body: { name?: string; ownerId?: string } = {}
      if (name.trim() && name.trim() !== server.name) body.name = name.trim()
      if (ownerId && ownerId !== server.owner?.id) body.ownerId = ownerId
      if (!body.name && !body.ownerId) { onClose(); return }
      const res = await adminApi.updateServer(server.id, body)
      onSaved(res.data)
    } catch (err: any) {
      setError(err.response?.data?.error?.toString?.() || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-card rounded-xl shadow-2xl border border-border w-[480px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-foreground font-semibold">Edit server</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {error && (
            <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>
          )}
          <Field label="Server name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </Field>
          <Field label="Owner">
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">— Select owner —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username} ({u.email})
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function UserEditModal({
  user,
  onClose,
  onSaved,
}: {
  user: UserItem
  onClose: () => void
  onSaved: (u: Partial<UserItem> & { id: string }) => void
}) {
  const [form, setForm] = useState({
    username: user.username,
    email: user.email,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    role: user.role || 'USER',
    isAdmin: !!user.isAdmin,
    aboutMe: user.aboutMe || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await adminApi.updateUser(user.id, form)
      onSaved(res.data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-card rounded-xl shadow-2xl border border-border w-[480px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-foreground font-semibold">Edit user</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-3">
          {error && (
            <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{error}</div>
          )}
          <Field label="Username">
            <input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </Field>
          <Field label="Email">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>
            <Field label="Last name">
              <input
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>
          </div>
          <Field label="Role">
            <input
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="USER, MANAGER, ..."
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
          </Field>
          <Field label="About">
            <textarea
              value={form.aboutMe}
              onChange={(e) => setForm({ ...form, aboutMe: e.target.value })}
              rows={3}
              className="w-full bg-muted text-foreground rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.isAdmin}
              onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })}
            />
            System administrator
          </label>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  )
}

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number; icon: any; color: string }) {
  return (
    <div className="bg-card rounded-lg p-4 flex items-center gap-3">
      <Icon size={24} className={color} />
      <div>
        <div className="text-2xl font-bold text-foreground">{value}</div>
        <div className="text-muted-foreground text-xs">{label}</div>
      </div>
    </div>
  )
}
